import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

import type { Metadata } from 'next';

export interface ServiceMetadataResult {
  metadata: Metadata;
  schemaMarkup?: string;
  isVisible?: boolean;
}

export async function buildServiceMetadataBySlug(slug: string): Promise<ServiceMetadataResult> {
  const baseUrl = process.env.NEXT_PUBLIC_APP_URL || 'https://www.makemybrandlabs.com';
  const normalized = String(slug).trim().replace(/^\/+/, '');
  
  // Store the original slug for canonical URL (should match the actual route)
  const canonicalSlug = normalized;

  try {
    // Try fetching with the exact slug first
    let res = await fetch(`${baseUrl}/api/service-meta/${normalized}`, { cache: 'no-store' });

    // If not found and slug starts with 'industries/', try without the prefix as fallback
    if (!res.ok && normalized.startsWith('industries/')) {
      const slugWithoutPrefix = normalized.replace(/^industries\//, '');
      res = await fetch(`${baseUrl}/api/service-meta/${slugWithoutPrefix}`, { cache: 'no-store' });
    }

    if (!res.ok) {
      throw new Error('not found');
    }

    const data = await res.json();

    const title = data.metaTitle || data.serviceName;
    const description = data.metaDescription || '';
    // Use canonicalUrl from data if provided, otherwise use the original slug (which matches the route)
    const canonicalUrl = data.canonicalUrl || `${baseUrl}/${canonicalSlug}`;
    const ogTitle = data.ogTitle || title;
    const ogDescription = data.ogDescription || description;
    const ogImage = data.ogImage;
    const twitterTitle = data.twitterTitle || ogTitle;
    const twitterDescription = data.twitterDescription || ogDescription;
    const twitterImage = data.twitterImage || ogImage;

    // Determine robots directive based on `indexed` flag from service_meta
    // - true / undefined => index,follow (default)
    // - false            => noindex,nofollow
    const shouldIndex = !(
      data.indexed === false ||
      data.indexed === 'false' ||
      data.indexed === 'noindex'
    );

    // Helper function to convert relative URLs to absolute URLs
    const makeAbsoluteUrl = (url: string | undefined): string | undefined => {
      if (!url) return undefined;
      // If URL is already absolute (starts with http:// or https://), return as is
      if (url.startsWith('http://') || url.startsWith('https://')) return url;
      // If URL is relative (starts with /), prepend base URL
      if (url.startsWith('/')) return `${baseUrl}${url}`;
      // Otherwise return as is
      return url;
    };

    const metadata: Metadata = {
      title,
      description,
      openGraph: {
        title: ogTitle,
        description: ogDescription,
        url: canonicalUrl,
        images: ogImage ? [makeAbsoluteUrl(ogImage)!] : [],
      },
      twitter: {
        card: 'summary_large_image',
        title: twitterTitle,
        description: twitterDescription,
        images: twitterImage ? [makeAbsoluteUrl(twitterImage)!] : [],
      },
      alternates: { canonical: canonicalUrl },
      robots: shouldIndex ? 'index,follow' : 'noindex,nofollow',
    };



    return {
      metadata,
      schemaMarkup: data.schemaMarkup,
      isVisible: data.isVisible !== false,
    };
  } catch {
    // Fallback metadata when no service meta is found in database
    // Use the original slug for canonical URL to match the actual route
    const fallbackCanonicalUrl = `${baseUrl}/${canonicalSlug}`;

    return {
      metadata: {
        alternates: { canonical: fallbackCanonicalUrl },
      } as Metadata,
    };
  }
}